import { useContext } from 'react';
import { UserContext, useEventCallback } from '@eas/common-web';
import { Decision, Me } from '@models';
import { Role, UIElement } from '@enums';
import {
  DecisionAccessKey,
  DecisionAccessLevel,
  decisionAccessMap,
  decisionElementToAccessKey,
  isExecutableAccess,
  isReadableAccess,
  isWritableAccess,
} from './decision-access-map';

export const DECISIONS_ACCESS = [
  Role.LUSTRUM, // Lustrum
  Role.HEAD_OSV, // Vedoucí OSV
  Role.INDEX, // Rejstřík
  Role.LAWYER, // Právník
  Role.SECRETARY, // Tajemnice
  Role.JUDGE, // Soudce
  Role.ASSISTANT, // Asistent
  Role.FILE_ROOM, // Spisovna
  Role.ANALYST, // Analytik
  Role.ANONYMIZATION, // Anonymizace
  Role.PUBLICATION, // Zveřejnění
  Role.READER, // Čtenář
  Role.ADVISER, // Poradce - sbírky
];

const accessLevelPriority: Record<DecisionAccessLevel, number> = {
  none: 0,
  read: 1,
  execute: 2,
  execute_own: 2,
  write: 3,
};

function resolveDecisionAccessKey(
  elementOrAccessKey: UIElement.Decision | DecisionAccessKey
) {
  return (
    decisionElementToAccessKey[elementOrAccessKey as UIElement.Decision] ??
    (elementOrAccessKey as DecisionAccessKey)
  );
}

function getDecisionAccessLevel(
  decision: Decision | null,
  accessKey: DecisionAccessKey,
  userRoles: Me['userRoles'] = []
): DecisionAccessLevel {
  if (!decision?.state) {
    return 'none';
  }

  return userRoles.reduce<DecisionAccessLevel>((highestLevel, { role }) => {
    const level =
      decisionAccessMap[role!]?.[decision.state!]?.[accessKey] ?? 'none';

    return accessLevelPriority[level] > accessLevelPriority[highestLevel]
      ? level
      : highestLevel;
  }, 'none');
}

export function useDecisionPermission(decision: Decision | null) {
  const { user } = useContext<UserContext<Me>>(UserContext);

  const isSuperAdmin = (user?.userRoles ?? []).some(
    (r) => r.role === Role.SUPERADMIN
  );

  const getAccessLevel = useEventCallback(
    (elementOrAccessKey: UIElement.Decision | DecisionAccessKey) => {
      const accessKey = resolveDecisionAccessKey(elementOrAccessKey);

      if (isSuperAdmin) {
        return 'write' as DecisionAccessLevel;
      }

      return getDecisionAccessLevel(decision, accessKey, user?.userRoles);
    }
  );

  const canRead = useEventCallback(
    (elementOrAccessKey: UIElement.Decision | DecisionAccessKey) => {
      if (isSuperAdmin) {
        return true;
      }

      return isReadableAccess(getAccessLevel(elementOrAccessKey));
    }
  );

  const canWrite = useEventCallback(
    (elementOrAccessKey: UIElement.Decision | DecisionAccessKey) => {
      if (isSuperAdmin) {
        return true;
      }

      return isWritableAccess(getAccessLevel(elementOrAccessKey));
    }
  );

  const canExecute = useEventCallback(
    (elementOrAccessKey: UIElement.Decision | DecisionAccessKey) => {
      if (isSuperAdmin) {
        return true;
      }

      // execute_own se dle 01-design.md ve FE povolí; autoritou zůstává BE.
      return isExecutableAccess(getAccessLevel(elementOrAccessKey));
    }
  );

  const hasPermission = useEventCallback((element: UIElement.Decision) => {
    if (isSuperAdmin) {
      return true;
    }

    const accessKey = decisionElementToAccessKey[element];

    if (accessKey?.endsWith('_op')) {
      return canExecute(element);
    }

    return canWrite(element);
  });

  return { hasPermission, canRead, canWrite, canExecute, getAccessLevel };
}
